import { useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";

import { notification } from "antd";
import { useLanguage } from "~/hooks/useLanguage";
import { loginDtoToViewModel } from "~/pages/login/helpers/mapper";
import { useAuthenticationContext } from "~/providers/AuthenticationProvider";
import { useSSOContext } from "~/pages/login/providers/MsalSSOProvider";
import { loginWithAzure } from "../services/loginWithAzure";
import { LoginError } from "~/pages/login/constants/error";
import { IntlMessageID } from "~/providers/I18nProvider";
import { PAGE_HOME } from "~/constants/page";

export const useLoginWithAzure = () => {
  const { ssoData, msalConfig } = useSSOContext();
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);

  const { formatMessage } = useLanguage();
  const { handleSetToken, setUserCompanyInfos } = useAuthenticationContext();

  const removeMsalStorage = useCallback(() => {
    sessionStorage.removeItem("msal.interaction.status");
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    msalConfig?.browserStorage?.clear();
  }, [msalConfig]);

  const handleLoginWithAzure = useCallback(async () => {
    if (!ssoData) return;

    try {
      setLoading(true);
      await instance
        .loginPopup({
          scopes: ["User.Read"],
          prompt: "select_account",
        })
        .then(async (result) => {
          const resp = await loginWithAzure(result.accessToken, result.idToken);
          const userCompanyInfos = loginDtoToViewModel(
            resp.userCompaniesInfo,
            resp.userName,
          );

          if (userCompanyInfos.length === 1) {
            handleSetToken(userCompanyInfos[0].token);
            if (window.location.pathname !== PAGE_HOME) {
              window.location.href = PAGE_HOME;
            }
          } else {
            setUserCompanyInfos(userCompanyInfos);
          }
        })
        .finally(() => removeMsalStorage());
    } catch (error) {
      if (
        (error as unknown as { errorCode: string }).errorCode !==
        "user_cancelled"
      ) {
        if (LoginError[error as string]) {
          notification.error({
            message: formatMessage(LoginError[error as string].message),
            description: LoginError[error as string].description
              ? formatMessage(
                  LoginError[error as string].description as IntlMessageID,
                )
              : undefined,
          });
        } else {
          notification.error({
            message: formatMessage("Login.Error.AzureSSOLoginFailed"),
          });
        }
      }
    } finally {
      setLoading(false);
    }
  }, [
    ssoData,
    instance,
    removeMsalStorage,
    setUserCompanyInfos,
    formatMessage,
    handleSetToken,
  ]);

  return { loading, handleLoginWithAzure };
};
